import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AppHeader } from "./AppHeader";
import { ModuleQuickNav } from "./ModuleQuickNav";
import { PageTransition } from "./PageTransition";
import { AppBreadcrumb } from "./AppBreadcrumb";
import { LicenceWarningBanner } from "./LicenceWarningBanner";
import { LicenceExpiredScreen } from "./LicenceExpiredScreen";
import { useLicence } from "@/hooks/useLicence";
import { ViewModeProvider } from "@/contexts/ViewModeContext";
import { useActiveOrg } from "@/contexts/ActiveOrgContext";
import { Skeleton } from "@/components/ui/skeleton";
import { ModuleFavoritesProvider } from "@/hooks/useModuleFavorites";
import { useEffect } from "react";

export function AppLayout() {
  const { loading: licenceLoading, isExpired, showWarning, daysUntilExpiry } = useLicence();
  const { activeOrg, loading: orgLoading } = useActiveOrg();
  const navigate = useNavigate();

  // Redirect to hub if no org selected
  useEffect(() => {
    if (!orgLoading && !activeOrg) {
      navigate("/hub");
    }
  }, [orgLoading, activeOrg, navigate]);

  if (licenceLoading || orgLoading) {
    return (
      <div className="flex flex-col h-screen">
        <div className="h-14 bg-card border-b border-border" />
        <main className="flex-1 p-8 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
          <div className="grid grid-cols-4 gap-4 mt-8">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
          </div>
        </main>
      </div>
    );
  }

  if (isExpired) return <LicenceExpiredScreen />;
  if (!activeOrg) return null;

  return (
    <ViewModeProvider>
      <ModuleFavoritesProvider>
        <div className="flex flex-col h-screen overflow-hidden">
          <AppHeader />
          <ModuleQuickNav />
          <AppBreadcrumb />
          {showWarning && daysUntilExpiry !== null && (
            <LicenceWarningBanner daysUntilExpiry={daysUntilExpiry} />
          )}
          <main className="flex-1 overflow-y-auto">
            <AnimatePresence mode="wait">
              <PageTransition>
                <Outlet />
              </PageTransition>
            </AnimatePresence>
          </main>
        </div>
      </ModuleFavoritesProvider>
    </ViewModeProvider>
  );
}
